import React, { useState } from "react";
import { Link } from "react-router-dom";
import { statusBadgeClass } from "@/lib/format";
import { HelpCircle, ChevronLeft } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function StatusBadge({ status, ...props }) {
    return (
        <span className={statusBadgeClass(status)} data-testid={`status-${status}`} {...props}>
            {status}
        </span>
    );
}

/**
 * Inline help bubble — small `?` icon that pops a tooltip on hover/click.
 *
 * Use it next to form labels that need a sentence of context, e.g.
 *   <HelpIcon text="What NexTerminal does with this field…" />
 */
export function HelpIcon({ text, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <span className="relative inline-flex items-center ml-1.5 align-middle"
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}>
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
                data-testid={testid}
                className="text-cm-dim hover:text-cm-brand transition-colors"
            >
                <HelpCircle size={12} />
            </button>
            {open && (
                <div className="absolute left-5 top-1/2 -translate-y-1/2 z-50 w-72 bg-cm-surface border border-cm-line2 px-3 py-2 text-xs text-cm-text leading-relaxed shadow-xl">
                    {text}
                </div>
            )}
        </span>
    );
}

export function BackLink({ to, label = "Back", testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-cm-muted hover:text-cm-brand transition-colors mb-2"
        >
            <ChevronLeft size={12} /> {label}
        </Link>
    );
}

export function PageHeader({ title, subtitle, action, testid, back, backLabel }) {
    return (
        <div
            className="px-4 sm:px-6 py-4 sm:py-5 border-b border-cm-line flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 bg-cm-surface"
            data-testid={testid}
        >
            <div className="min-w-0">
                {back && <BackLink to={back} label={backLabel} />}
                <h1 className="cm-h1 break-words" data-testid={`${testid}-title`}>
                    {title}
                </h1>
                {subtitle && (
                    <div className="text-sm text-cm-muted mt-1 break-words">{subtitle}</div>
                )}
            </div>
            {action && (
                <div className="flex items-center gap-2 flex-wrap shrink-0">{action}</div>
            )}
        </div>
    );
}

/** Card with a standard header row: optional eyebrow, title, optional actions. */
export function Section({ title, eyebrow, icon: Icon, actions, children, testid, className = "", bodyCls = "" }) {
    return (
        <div className={`cm-card ${className}`} data-testid={testid}>
            {(title || eyebrow || actions) && (
                <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-cm-line">
                    {Icon && <Icon size={16} strokeWidth={1.5} className="text-cm-muted shrink-0" />}
                    <div className="min-w-0 flex-1">
                        {eyebrow && <div className="cm-eyebrow">{eyebrow}</div>}
                        {title && <div className="cm-h4">{title}</div>}
                    </div>
                    {actions && <div className="flex items-center gap-2 flex-wrap shrink-0">{actions}</div>}
                </div>
            )}
            <div className={bodyCls || "p-4"}>{children}</div>
        </div>
    );
}

/** Compact stat tile used in KPI rows. */
export function Stat({ label, value, sub, testid, valueCls }) {
    return (
        <div className="cm-card p-4" data-testid={testid}>
            <div className="cm-label">{label}</div>
            <div className={"mt-2 font-mono text-2xl tracking-tight " + (valueCls || "text-cm-text")}>{value}</div>
            {sub && <div className="text-xs text-cm-muted mt-1">{sub}</div>}
        </div>
    );
}

export function Empty({ title = "Nothing here yet", hint, testid }) {
    return (
        <div
            className="cm-card p-10 text-center"
            data-testid={testid || "empty-state"}
        >
            <div className="cm-h3 text-cm-text">{title}</div>
            {hint && <div className="text-sm text-cm-muted mt-2">{hint}</div>}
        </div>
    );
}

export function Spinner({ label }) {
    return (
        <div className="flex items-center gap-3 text-cm-muted text-sm">
            <span className="inline-block w-3 h-3 border border-cm-line2 border-t-cm-brand rounded-full animate-spin" />
            {label || "Loading…"}
        </div>
    );
}

export function KV({ k, v, mono = false, testid }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b border-cm-line last:border-b-0">
            <div className="cm-label">{k}</div>
            <div
                className={`text-sm text-cm-text text-right break-all ${mono ? "font-mono" : ""}`}
                data-testid={testid}
            >
                {v ?? "—"}
            </div>
        </div>
    );
}

/** Small horizontal progress bar with percentage label. */
export function ProgressBar({ value, testid, className = "w-24" }) {
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <div className={`${className} h-1 bg-cm-surface2 border border-cm-line`}>
                <div className="h-full bg-cm-brand" style={{ width: `${value || 0}%` }} />
            </div>
            <span className="text-cm-muted text-xs font-mono">{value ?? 0}%</span>
        </div>
    );
}

/** Form field wrapper: label + optional help icon + children. */
export function Field({ label, hint, help, children, testid, className = "" }) {
    return (
        <div className={className} data-testid={testid}>
            {label && (
                <div className="flex items-center mb-1.5">
                    <span className="cm-label">{label}</span>
                    {help && <HelpIcon text={help} />}
                </div>
            )}
            {children}
            {hint && <div className="text-xs text-cm-dim mt-1.5 leading-relaxed">{hint}</div>}
        </div>
    );
}

/** Segmented control (single-select button group). */
export function Segmented({ options, value, onChange, testid, className = "" }) {
    return (
        <div className={`inline-flex border border-cm-line2 bg-cm-bg ${className}`} data-testid={testid} role="tablist">
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        data-testid={o.testid}
                        onClick={() => onChange(o.value)}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                            active
                                ? "bg-cm-brand text-cm-bg font-bold"
                                : "text-cm-muted hover:text-cm-text hover:bg-cm-surface2"
                        }`}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/** Styled confirmation dialog — replaces native window.confirm(). */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = "Confirm", tone = "danger", busy, onConfirm, testid }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-cm-surface border-cm-line2 text-cm-text sm:rounded-none">
                <DialogHeader>
                    <DialogTitle className="font-display tracking-tight text-cm-text">{title}</DialogTitle>
                    {description && (
                        <DialogDescription className="text-cm-muted">{description}</DialogDescription>
                    )}
                </DialogHeader>
                <DialogFooter>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="cm-btn-secondary text-xs"
                        data-testid={testid ? `${testid}-cancel` : undefined}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onConfirm}
                        data-testid={testid ? `${testid}-confirm` : undefined}
                        className={
                            tone === "danger"
                                ? "cm-btn-danger text-xs disabled:opacity-50"
                                : "cm-btn-primary text-xs disabled:opacity-50"
                        }
                    >
                        {busy ? "Working…" : confirmLabel}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
